import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react-native'
import type { ReactNode } from 'react'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { Text, TouchableRipple, useTheme } from 'react-native-paper'
import type { Notice, Severity } from '@/stores/notice-store'

export type NoticeItemVariant = 'banner' | 'toast'

type Props = {
    notice: Notice
    variant: NoticeItemVariant
    onDismiss: () => void
    showCloseButton: boolean
}

// Paper M3 has no warningContainer token, so `warning` reuses `errorContainer`.
// If/when a distinct warning palette is needed, extend the theme and update this map.
const SEVERITY_CONTAINER: Record<
    Severity,
    'errorContainer' | 'secondaryContainer' | 'primaryContainer' | 'tertiaryContainer'
> = {
    error: 'errorContainer',
    warning: 'errorContainer',
    info: 'secondaryContainer',
    success: 'primaryContainer',
    neutral: 'tertiaryContainer',
}

const SEVERITY_ON_CONTAINER: Record<
    Severity,
    'onErrorContainer' | 'onSecondaryContainer' | 'onPrimaryContainer' | 'onTertiaryContainer'
> = {
    error: 'onErrorContainer',
    warning: 'onErrorContainer',
    info: 'onSecondaryContainer',
    success: 'onPrimaryContainer',
    neutral: 'onTertiaryContainer',
}

export function NoticeItem({ notice, variant, onDismiss, showCloseButton }: Props) {
    const theme = useTheme()
    const bg = theme.colors[SEVERITY_CONTAINER[notice.severity]]
    const fg = theme.colors[SEVERITY_ON_CONTAINER[notice.severity]]

    const iconNode = renderIcon(notice, fg)

    return (
        <View style={[variant === 'toast' ? styles.toast : styles.banner, { backgroundColor: bg }]}>
            <View style={styles.row}>
                <View style={styles.iconWrap}>{iconNode}</View>
                <View style={styles.body}>
                    {notice.title ? (
                        <Text variant="labelLarge" style={[styles.title, { color: fg }]}>
                            {notice.title}
                        </Text>
                    ) : null}
                    <Text
                        variant={notice.title ? 'bodySmall' : 'labelMedium'}
                        style={{ color: fg }}
                    >
                        {notice.message}
                    </Text>
                </View>
                {showCloseButton ? (
                    <TouchableRipple
                        onPress={onDismiss}
                        style={styles.closeButton}
                        accessibilityLabel="Dismiss"
                        accessibilityRole="button"
                    >
                        <X size={16} color={fg} />
                    </TouchableRipple>
                ) : null}
            </View>
            {notice.actions && notice.actions.length > 0 ? (
                <View
                    style={[
                        styles.actions,
                        notice.actions.length === 1 ? styles.actionsRight : styles.actionsBetween,
                    ]}
                >
                    {notice.actions.map(action => (
                        <TouchableRipple
                            key={action.label}
                            onPress={() => {
                                action.onPress()
                                if (action.dismissOnPress !== false) onDismiss()
                            }}
                            style={styles.action}
                            accessibilityRole="button"
                        >
                            <Text variant="labelMedium" style={[styles.actionText, { color: fg }]}>
                                {action.label}
                            </Text>
                        </TouchableRipple>
                    ))}
                </View>
            ) : null}
        </View>
    )
}

function renderIcon(notice: Notice, fg: string): ReactNode {
    const icon = notice.icon ?? defaultIcon(notice.severity)
    if (icon === 'spinner') {
        return <ActivityIndicator size={16} color={fg} />
    }
    const Icon = icon
    return <Icon size={16} color={fg} />
}

function defaultIcon(severity: Severity) {
    switch (severity) {
        case 'error':
            return AlertCircle
        case 'warning':
            return AlertTriangle
        case 'success':
            return CheckCircle
        case 'info':
        case 'neutral':
            return Info
    }
}

const styles = StyleSheet.create({
    banner: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        gap: 6,
    },
    toast: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 6,
        borderRadius: 8,
        maxWidth: 360,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 6,
        elevation: 4,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    iconWrap: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    body: {
        flex: 1,
    },
    title: {
        fontWeight: '600',
    },
    closeButton: {
        padding: 4,
        borderRadius: 4,
    },
    actions: {
        flexDirection: 'row',
        gap: 8,
    },
    actionsRight: {
        justifyContent: 'flex-end',
    },
    actionsBetween: {
        justifyContent: 'space-between',
    },
    action: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 4,
    },
    actionText: {
        fontWeight: '600',
    },
})
